<template>
  <v-dialog v-model="show" max-width="512" persistent>
    <div class="modal-container">
      <div class="modal-header">
        <div class="modal-text-container">
          <h1 class="modal-text-title">Add promo code</h1>
          <div class="modal-text-info">
            <p class="modal-text-info-step">Step {{ step }}</p>
            <p class="modal-text-info-step">{{ stepper.find(s => s.id === step).text }}</p>
          </div>
        </div>
        <v-icon color="#979797" class="close-icon" @click="cancel">close</v-icon>
      </div>
      <div v-if="step === 1" class="discount-options-container">
        <div class="discount-option" @click="handleDiscountOptionSelect('PERCENTAGE')">
          <img
            class="discount-option-icon"
            src="/assets/discount-percentage-icon.svg"
            alt="Discount percentage"
          />
          <div class="discount-option-title">Discount percentage</div>
        </div>
        <div class="discount-option" @click="handleDiscountOptionSelect('AMOUNT')">
          <img
            class="discount-option-icon"
            src="/assets/discount-amount-icon.svg"
            alt="Discount amount"
          />
          <h4 class="discount-option-title">Discount amount</h4>
        </div>
      </div>
      <div v-else class="main-container">
        <div v-if="step === 2" class="form-container">
          <select-field-multiple
            v-model="form.eventIds"
            :validator="vs.form.eventIds"
            label="Event*"
            :items="allEvents"
            item-render="title"
            item-select="id"
          />
          <select-field-multiple
            v-if="performances.length"
            v-model="form.performanceIds"
            :validator="vs.form.performanceIds"
            :error-messages="{
              selectedPerformances: 'Please select performances for all selected events',
            }"
            label="Performance*"
            :items="performances"
            item-render="title"
            item-select="id"
          />
          <select-field-multiple
            v-model="form.orderItemIds"
            :disabled="!form.eventIds.length"
            :validator="vs.form.orderItemIds"
            :error-messages="{
              selectedOrderItems: 'Please select tickets for all selected events',
            }"
            label="Ticket*"
            :items="orderItems"
            item-render="name"
            item-select="id"
          />
        </div>
        <div v-if="step === 3" class="form-container">
          <text-field
            id="name"
            v-model.trim="form.name"
            label="Name*"
            type="text"
            :validator="vs.form.name"
          />
          <text-field
            id="code"
            v-model.trim="form.code"
            label="Unique Promo code*"
            type="text"
            :validator="vs.form.code"
          />
          <text-field
            v-if="form.discountType === 'PERCENTAGE'"
            id="discountPercentage"
            v-model.number="form.discountPercentage"
            label="Discount percentage*"
            type="number"
            :validator="vs.form.discountPercentage"
            append-icon="percent"
            prepend-icon="remove"
          />
          <text-field
            v-else
            id="discountAmount"
            v-model.number="form.discountAmount"
            label="Discount amount*"
            type="number"
            :validator="vs.form.discountAmount"
            append-icon="euro"
            prepend-icon="remove"
          />
          <text-field
            id="quantityPerTicket"
            v-model.number="form.quantityPerTicket"
            label="Quantity per ticket*"
            type="number"
            :disabled="form.unlimited"
            :validator="vs.form.quantityPerTicket"
          />
          <checkbox v-model="form.unlimited" :validator="vs.form.unlimited" color="#0277bd">
            <p class="unlimited-checkbox">Unlimited quantity</p>
          </checkbox>
          <text-field
            id="ticketsPerOrder"
            v-model.number="form.ticketsPerOrder"
            label="Max tickets per order*"
            type="number"
            :validator="vs.form.ticketsPerOrder"
          />
          <div class="dates-container">
            <datetime-field
              v-model="form.from"
              :validator="vs.form.from"
              label="Start date*"
              :min="new Date().toISOString()"
              :error-messages="{
                minDate: 'Start date must be a future date',
                maxDate: 'Start date should be before the end of the event',
              }"
            />
            <datetime-field
              v-model="form.to"
              :validator="vs.form.to"
              label="End date*"
              :min="form.from ? new Date(form.from).toISOString() : new Date().toISOString()"
              :error-messages="{
                minDate: 'Start date must be a future date',
                maxDate: 'Start date should be before the end of the event',
              }"
            />
          </div>
        </div>
        <div class="buttons-container">
          <main-button outline @click="handleBack">back</main-button>
          <main-button :disabled="!canGoNext" @click="handleNext">
            {{ step === 2 ? 'next' : 'finish' }}
          </main-button>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { intersection, isEqual, pick } from 'lodash'

const stepper = [
  {
    id: 1,
    text: 'Choose Promo code type',
  },
  {
    id: 2,
    text: 'Choose Event and Ticket',
  },
  {
    id: 3,
    text: 'Add Promo code details',
  },
]
export default {
  name: 'create-promo-code-modal',
  props: {
    show: Boolean,
    initialValues: Object,
    vs: Object,
    allEvents: Array,
    performances: Array,
    orderItems: Array,
  },
  data() {
    return { form: { ...this.initialValues }, stepper, step: 1 }
  },
  computed: {
    canGoNext() {
      return this.step === 2
        ? !this.vs.form.eventIds.$invalid &&
            !this.vs.form.performanceIds.$invalid &&
            !this.vs.form.orderItemIds.$invalid
        : !this.vs.form.name.$invalid &&
            !this.vs.form.code.$invalid &&
            !this.vs.form.quantityPerTicket.$invalid &&
            !this.vs.form.unlimited.$invalid &&
            !this.vs.form.ticketsPerOrder.$invalid &&
            !this.vs.form.discountPercentage.$invalid &&
            !this.vs.form.discountAmount.$invalid &&
            !this.vs.form.from.$invalid &&
            !this.vs.form.to.$invalid
    },
  },
  watch: {
    form: {
      handler(val) {
        const newForm = { ...val }

        newForm.performanceIds = intersection(
          newForm.performanceIds,
          this.performances
            .filter(p => newForm.eventIds.includes(p.eventTemplate.id))
            .map(p => p.id),
        )
        newForm.orderItemIds = intersection(
          newForm.orderItemIds,
          this.orderItems
            .filter(oi =>
              [
                ...newForm.eventIds,
                ...(newForm.performanceIds ? newForm.performanceIds : []),
              ].includes(oi.eventId),
            )
            .map(p => p.id),
        )

        newForm.quantityPerTicket = newForm.unlimited ? null : newForm.quantityPerTicket

        if (!isEqual(this.form, newForm)) {
          this.form = newForm
        } else {
          this.$emit('update-form', newForm)
        }
      },
      deep: true,
    },
  },
  methods: {
    closeDialog() {
      this.$emit('update:show', false)
    },
    cancel() {
      this.closeDialog()
      this.vs.$reset()
      this.form = { eventIds: [], performanceIds: [], orderItemIds: [] }
      this.step = 1
    },
    handleDiscountOptionSelect(type) {
      this.form = { ...this.form, discountType: type }
      this.step = 2
    },
    handleBack() {
      if (this.step === 2) {
        this.form = { eventIds: [], performanceIds: [], orderItemIds: [] }
      } else {
        this.form = pick(this.form, ['discountType', 'eventIds', 'performanceIds', 'orderItemIds'])
      }
      this.step -= 1
      this.vs.$reset()
    },
    handleNext() {
      if (this.step === 3) {
        this.$emit('create-promo-code')
      } else {
        this.step += 1
      }
    },
  },
}
</script>

<style scoped>
.modal-container {
  background: #ffffff;
  box-shadow: 0px 0px 24px rgba(51, 51, 51, 0.2);
  border-radius: 12px;
  padding: 24px 24px 32px 24px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.modal-header {
  display: flex;
  align-items: baseline;
}
.modal-text-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.modal-text-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #0277bd;
}
.modal-text-info {
  display: flex;
  flex-direction: column;
}
.modal-text-info-step {
  margin: 0%;
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  color: #979797;
}
.close-icon {
  cursor: pointer;
  user-select: none;
}
.discount-options-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.discount-option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 2rem;
  gap: 12px;
  background: #f5f5f5;
  border: 2px solid #d9d9d9;
  border-radius: 12px;
}
.discount-option:hover {
  cursor: pointer;
  background: #e6f1f8;
  border: 2px solid #cee4f2;
  border-radius: 12px;
}
.discount-option:active {
  cursor: pointer;
  background: #cee4f2;
  border: 2px solid #0277bd;
  border-radius: 12px;
}
.discount-option-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #0277bd;
}
.discount-option-icon {
  user-select: none;
}
.main-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.form-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 0%;
}
.buttons-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
.dates-container {
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
}
.unlimited-checkbox {
  margin-bottom: 0;
}
</style>
